import Cookies from 'js-cookie'

import { LOGIN_SOURCE_KEY, LoginSource } from '@/consts/team'
import { isValidLoginSource } from '@/consts/token'
import { removeCookieFromAllScopes, setHostCookie } from '@/utils/cookieStorage'

/**
 * Set login source to cookie
 * @throws Error if cookie operation fails (critical for auth state)
 */
export function setLoginSource(source: LoginSource): void {
  try {
    setHostCookie(LOGIN_SOURCE_KEY, source, { expires: 30 })
  } catch (error) {
    console.error('Failed to set login source:', error)
    // Re-throw for critical auth operations - caller should handle
    throw new Error(`Failed to set login source: ${error}`)
  }
}

/**
 * Get login source from cookie with validation
 * @returns Valid LoginSource or null if not found/invalid
 */
export function getLoginSource(): LoginSource | null {
  try {
    const source = Cookies.get(LOGIN_SOURCE_KEY)

    // Validate the cookie value before returning
    if (source && isValidLoginSource(source)) {
      return source as LoginSource
    }

    // Invalid or missing value - clear corrupted cookie
    if (source) {
      console.warn('Invalid login source value in cookie, clearing:', source)
      removeCookieFromAllScopes(LOGIN_SOURCE_KEY)
    }

    return null
  } catch (error) {
    console.error('Failed to get login source:', error)
    return null
  }
}

/**
 * Clear login source from cookie
 */
export function clearLoginSource(): void {
  try {
    removeCookieFromAllScopes(LOGIN_SOURCE_KEY)
  } catch (error) {
    console.error('Failed to clear login source:', error)
  }
}

/**
 * Check if current user is team-only user (logged in from team login page)
 */
export function isTeamOnlyUser(): boolean {
  return getLoginSource() === LoginSource.Team
}
